/* Startup Burn Rate & Cash Runway Calculator Logic — Math, Charts & CSV Export */

let sbrDonutChart = null;
let sbrGrowthChart = null;

function getSbrInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    cash: getVal('sbrCash', 10000000),
    grossBurn: getVal('sbrGrossBurn', 1500000),
    revenue: getVal('sbrRevenue', 500000),
  };
}

function updateSbrUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('cash', params.cash);
    url.searchParams.set('grossBurn', params.grossBurn);
    url.searchParams.set('revenue', params.revenue);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseSbrUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const cash = searchParams.get('cash');
  const grossBurn = searchParams.get('grossBurn');
  const revenue = searchParams.get('revenue');

  return {
    cash: cash !== null ? +cash : null,
    grossBurn: grossBurn !== null ? +grossBurn : null,
    revenue: revenue !== null ? +revenue : null,
  };
}

function calculateSbr(params) {
  const { cash, grossBurn, revenue } = params;

  const netBurn = Math.max(0, grossBurn - revenue);

  let runwayMonths = 0;
  let isProfitable = false;

  if (netBurn === 0) {
    runwayMonths = Infinity;
    isProfitable = true;
  } else {
    runwayMonths = cash / netBurn;
  }

  const schedule = [];
  let balance = cash;
  const maxMonths = isProfitable ? 24 : Math.min(60, Math.ceil(runwayMonths));

  for (let m = 1; m <= maxMonths; m++) {
    const openingBalance = balance;
    balance = Math.max(0, balance - netBurn);

    schedule.push({
      month: m,
      openingBalance: Math.round(openingBalance),
      revenue: Math.round(revenue),
      grossBurn: Math.round(grossBurn),
      netBurn: Math.round(netBurn),
      closingBalance: Math.round(balance),
    });

    if (balance <= 0) break;
  }

  return {
    cash: Math.round(cash),
    grossBurn: Math.round(grossBurn),
    revenue: Math.round(revenue),
    netBurn: Math.round(netBurn),
    runwayMonths: isFinite(runwayMonths) ? +runwayMonths.toFixed(1) : Infinity,
    runwayYears: isFinite(runwayMonths) ? +(runwayMonths / 12).toFixed(1) : Infinity,
    isProfitable,
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('sbrCashDisplay', fmt(params.cash));
  setTxt('sbrGrossBurnDisplay', fmt(params.grossBurn));
  setTxt('sbrRevenueDisplay', fmt(params.revenue));
}

function renderSbrResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  if (result.isProfitable) {
    setTxt('sbrVerdictHeadline', 'Runway: Infinite (Profitable 🎉)');
    setTxt('sbrVerdictDetail', `Your monthly recurring revenue of ${fmt(params.revenue)} fully covers your gross operating expenses of ${fmt(params.grossBurn)}, generating positive cash flow.`);
    setTxt('sbrRunwayMetricValue', 'Infinite 🎉');
  } else {
    const mStr = `${result.runwayMonths} Months`;
    const yStr = `${result.runwayYears} Yrs`;

    setTxt('sbrVerdictHeadline', `Runway: ${mStr} (${yStr})`);
    setTxt('sbrVerdictDetail', `With a current cash reserve of ${fmt(params.cash)} and net monthly burn of ${fmt(result.netBurn)}, your startup has ${mStr} of runway remaining.`);
    setTxt('sbrRunwayMetricValue', mStr);
  }

  setTxt('sbrNetBurnStatValue', `${fmt(result.netBurn)}/mo`);
  setTxt('sbrGrossBurnStatValue', `${fmt(result.grossBurn)}/mo`);
  setTxt('sbrCashMetricValue', fmt(result.cash));
  setTxt('sbrNetBurnMetricValue', `${fmt(result.netBurn)}/mo`);

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.revenue, result.netBurn, result.cash);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('Startup Burn Rate Chart rendering error:', err);
  }
}

function renderDonutChart(revenue, netBurn, cash) {
  const ctx = document.getElementById('sbrDonutChart');
  if (!ctx) return;

  if (sbrDonutChart) sbrDonutChart.destroy();

  sbrDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Monthly Revenue Inflow', 'Net Monthly Cash Burn', 'Cash Reserve'],
      datasets: [
        {
          data: [revenue, netBurn, cash],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent'), '#3B82F6'],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('sbrGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Mo ${r.month}`);
  const balanceData = schedule.map((r) => r.closingBalance);

  if (sbrGrowthChart) sbrGrowthChart.destroy();

  sbrGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Remaining Cash Balance',
          data: balanceData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(239, 68, 68, 0.08)',
          fill: true,
          tension: 0.2,
          pointRadius: 3,
          borderWidth: 2.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('sbrScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => `
    <tr>
      <td>${row.month}</td>
      <td>${fmt(row.openingBalance)}</td>
      <td class="advantage-buy">+${fmt(row.revenue)}</td>
      <td class="advantage-renter">-${fmt(row.grossBurn)}</td>
      <td class="advantage-renter">-${fmt(row.netBurn)}</td>
      <td style="font-weight: 600;">${fmt(row.closingBalance)}</td>
    </tr>
  `).join('');
}

function exportSbrCsv() {
  const params = getSbrInputs();
  const result = calculateSbr(params);
  const headers = ['Month', 'Opening Cash Balance', 'Monthly Revenue Inflow', 'Gross Expenses', 'Net Monthly Burn', 'Closing Balance'];
  const rows = result.schedule.map((r) => [
    r.month, r.openingBalance, r.revenue, r.grossBurn, r.netBurn, r.closingBalance
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `startup-burn-rate-runway.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applySbrCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultCash = 10000000;
  let maxCash = 100000000;
  let stepCash = 500000;

  if (curr === 'INR') {
    defaultCash = 10000000;
    maxCash = 100000000;
    stepCash = 500000;
  } else if (curr === 'JPY') {
    defaultCash = 100000000;
    maxCash = 1000000000;
    stepCash = 5000000;
  }

  const cSlider = document.getElementById('sbrCash');
  if (cSlider) {
    cSlider.max = maxCash;
    cSlider.step = stepCash;
    cSlider.value = overrideParams.cash ?? defaultCash;
  }

  const gSlider = document.getElementById('sbrGrossBurn');
  if (gSlider && overrideParams.grossBurn !== null) {
    gSlider.value = overrideParams.grossBurn;
  }

  const rSlider = document.getElementById('sbrRevenue');
  if (rSlider && overrideParams.revenue !== null) {
    rSlider.value = overrideParams.revenue;
  }
}

function recalculateSbr() {
  const params = getSbrInputs();
  updateDisplays(params);
  updateSbrUrlParams(params);
  const result = calculateSbr(params);
  renderSbrResults(result, params);
}

function initSbrCalculator() {
  if (!document.getElementById('sbrCash')) return;

  const urlParams = parseSbrUrlParams();

  initCountry((country) => {
    applySbrCountryRanges(country, urlParams);
    recalculateSbr();
  });

  const inputs = ['sbrCash', 'sbrGrossBurn', 'sbrRevenue'];
  inputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', recalculateSbr);
      el.addEventListener('change', recalculateSbr);
    }
  });

  const exportBtn = document.getElementById('exportSbrCsv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportSbrCsv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initSbrCalculator();
});
